import {useMutation, useQueryClient} from '@github-ui/react-query'
import {
  getCurrentRepositoryNwo,
  getNavigationTabCountAuthoritativeVersion,
  getNavigationTabCountSnapshot,
  getNavigationTabCountTargetVersion,
  updateNavigationTabCount,
} from '@github-ui/global-navigation'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
  parseJSONWithBetterErrors,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

import {useMergeBoxPageDataQueryKey} from '../../page-data/loaders/use-merge-box-page-data'
import type {StateChangeMutationOptions} from './state-change-mutation-options'

export function useClosePullRequestMutation({onError, onSuccess, onMutate}: StateChangeMutationOptions = {}) {
  const apiURL = usePageDataUrl(PageData.closePullRequest)
  const mergeBoxQueryKey = useMergeBoxPageDataQueryKey()
  const queryClient = useQueryClient()

  return useMutation({
    onMutate: () => {
      return {
        rollback: onMutate?.(),
        repositoryNwo: getCurrentRepositoryNwo(),
        tabCount: getNavigationTabCountSnapshot('pull-requests'),
        targetVersion: getNavigationTabCountTargetVersion('pull-requests'),
        updateVersion: getNavigationTabCountAuthoritativeVersion('pull-requests'),
      }
    },
    mutationFn: async () => {
      const response = await fetchWithErrorHandling(apiURL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
      })
      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
      return json
    },
    onSuccess: (_data, _variables, {repositoryNwo, tabCount, targetVersion, updateVersion}) => {
      updateNavigationTabCount(
        'pull-requests',
        {
          type: 'delta',
          delta: -1,
          ...(tabCount?.exact ? {expectedCountAfterAuthoritativeUpdate: tabCount.count} : {}),
        },
        {
          repositoryNwo,
          expectedUpdateVersion: updateVersion,
          expectedTargetVersion: targetVersion,
        },
      )
      return Promise.all([
        onSuccess?.(),
        queryClient.refetchQueries({queryKey: mergeBoxQueryKey}, {cancelRefetch: false}),
      ])
    },
    onError: (e: Error, _variables, onMutateResult) => {
      onMutateResult?.rollback?.()
      onError?.(e)
    },
  })
}
